import React, { useState } from 'react';
import { Filter, X, Calendar, DollarSign } from 'lucide-react';

const REASON_TEMPLATES = {
  approval: [
    'İptal talebiniz politikalarımız dahilinde onaylanmıştır.',
    'Geçerli mazeret nedeniyle iptal talebiniz kabul edilmiştir.',
    'Sağlık nedenleri göz önünde bulundurularak onaylanmıştır.',
    'Hava koşulları nedeniyle iptal talebiniz onaylanmıştır.'
  ],
  rejection: [
    'İptal süresi geçtiği için talebiniz reddedilmiştir.',
    'İptal politikamız gereği bu aşamada iptal kabul edilememektedir.',
    'Belirtilen neden iptal politikamız kapsamında değildir.',
    'Etkinlik yaklaştığı için iptal yapılamamaktadır.'
  ]
};

export default function FilterPanel({ filters, onFiltersChange, onClose }) {
  const [localFilters, setLocalFilters] = useState(filters);

  const handleFilterChange = (key, value) => {
    setLocalFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    onFiltersChange(localFilters);
    onClose?.();
  };

  const handleResetFilters = () => {
    const resetFilters = {
      status: 'all',
      dateFrom: '',
      dateTo: '',
      refundMin: '',
      refundMax: ''
    };
    setLocalFilters(resetFilters);
    onFiltersChange(resetFilters);
  };

  const activeFiltersCount = Object.values(localFilters)?.filter(v => 
    v && v !== 'all' && v !== ''
  )?.length;

  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
          {activeFiltersCount > 0 && (
            <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
              {activeFiltersCount} active
            </span>
          )}
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>
      {/* Filter Options */}
      <div className="space-y-6">
        {/* Approval Status Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Approval Status
          </label>
          <div className="grid grid-cols-2 gap-2">
            {[
              { value: 'all', label: 'All Requests' },
              { value: 'pending', label: 'Pending' },
              { value: 'approved', label: 'Approved' },
              { value: 'rejected', label: 'Rejected' }
            ]?.map(option => (
              <button
                key={option?.value}
                onClick={() => handleFilterChange('status', option?.value)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  localFilters?.status === option?.value
                    ? 'bg-blue-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {option?.label}
              </button>
            ))}
          </div>
        </div>

        {/* Decision Date Range */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Calendar className="w-4 h-4 inline mr-1" />
            Decision Date Range
          </label>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">From</label>
              <input
                type="date"
                value={localFilters?.dateFrom || ''}
                onChange={(e) => handleFilterChange('dateFrom', e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">To</label>
              <input
                type="date"
                value={localFilters?.dateTo || ''}
                onChange={(e) => handleFilterChange('dateTo', e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>
          </div>
        </div>

        {/* Refund Amount Range */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <DollarSign className="w-4 h-4 inline mr-1" />
            Refund Amount Range (₺)
          </label>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Min</label>
              <input
                type="number"
                value={localFilters?.refundMin || ''}
                onChange={(e) => handleFilterChange('refundMin', e?.target?.value)}
                placeholder="0"
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Max</label>
              <input
                type="number"
                value={localFilters?.refundMax || ''}
                onChange={(e) => handleFilterChange('refundMax', e?.target?.value)}
                placeholder="Any"
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>
          </div>
        </div>
      </div>
      {/* Action Buttons */}
      <div className="flex gap-3 mt-6 pt-6 border-t border-gray-200">
        <button
          onClick={handleResetFilters}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
        >
          Reset
        </button>
        <button
          onClick={handleApplyFilters}
          className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
        >
          Apply Filters
        </button>
      </div>
    </div>
  );
}

export { REASON_TEMPLATES };